import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/services/api.service';
import {
  ContainerType,
  podcastIcon,
  Record,
  RecordAvailability,
  RecordSubject,
} from 'src/app/models/record';
import { AuthService } from 'src/app/services/auth.service';
import { Recommendation, RecommendationType } from 'src/app/models/recommendation';
import { ButtonsConfiguration, InfoButtonConfiguration } from 'src/app/models/buttons-config';
import { ConfigService } from 'src/app/services/config.service';
import { Util } from 'src/app/util';
import { Location } from '@angular/common';
import { SearchState } from 'src/app/models/search-model';
import { FromScreen } from 'src/app/models/from-screen';
import { LocaleService } from 'src/app/services/locale.service';
import { Observable } from 'rxjs';
import { map, pluck, tap } from 'rxjs/operators';
import { Rating } from 'src/app/models/rating';
import { Action, ActionType } from 'src/app/models/action';
import { User } from '../../models/user';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { CustomerType } from '../../models/client-configuration';
import { UserService } from 'src/app/services/user.service';
import { BroadcastService } from 'src/app/services/broadcast.service';
import { Collection } from '../../models/collection';
import { RssItem } from '../../models/rss';
import { Title } from '@angular/platform-browser';
import { JsonLDService } from 'src/app/services/jsonld.service';
import { DescendientesRecord, FascicleRecord, PhysicalRecord } from 'src/app/models/physical-record';
import { SEOService } from 'src/app/services/seo.service';
import { PlayerUrlAnnotationService } from 'src/app/services/api/player-url-annotation.service';
import { LoginDialogComponent } from '../../login/components/login-dialog/login-dialog.component';
import { ToastService } from '../../_core/services/toast.service';
import { CommentsFacade } from '../../info/state/comments/comments.facade';
import { toSignal } from '@angular/core/rxjs-interop';
import { Comment } from '../../info/services/comments/models/comment.model';
import { SubmitReplyEvent } from '../../info/components/comments/edit-comment/edit-comment.component';
import { generateId } from '../../_core/utils/generate-id';

@Component({
  selector: 'app-info',
  templateUrl: './info.component.html',
  styleUrls: ['./info.component.scss'],
})
export class InfoComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly toastService = inject(ToastService);
  private readonly commentsFacade = inject(CommentsFacade);

  readonly comments = toSignal(this.commentsFacade.comments$);
  readonly commentsCount = toSignal(this.commentsFacade.commentsCount$);
  readonly isLoadingComments = toSignal(this.commentsFacade.isLoading$);
  readonly isCreatingComment = toSignal(this.commentsFacade.isCreatingComment$);
  readonly commentsLastEvaluatedKey = toSignal(this.commentsFacade.lastEvaluatedKey$);
  readonly openTextareaId = toSignal(this.commentsFacade.openTextarea$);
  readonly textareaId = signal<string>(generateId());

  trackCommentsFn(_, comment: Comment) {
    return comment.id;
  }

  @ViewChild('description')
  description: ElementRef<HTMLParagraphElement>;

  @ViewChild('recordTitle')
  recordTitle: ElementRef;

  @ViewChild('accessibilityAutofocus')
  accessibilityAutofocus: ElementRef;

  private id: string;
  readonly PODCAST_ICON = podcastIcon;

  loading: boolean;
  user: User;
  record: Record;
  availability: RecordAvailability;
  rating: Rating;
  buttonsConfig: ButtonsConfiguration;
  state: SearchState;
  issueDate: string;
  containerType: ContainerType;
  rhId: string;
  rssItemSelectedFromUrl: boolean;
  selectedRssItem: RssItem;
  images: string[] = [];
  physicalParentTitle: string = '';
  physicalParentId: string;
  physicalAvailabilityItems: PhysicalRecord[] = [];
  physicalLowerLevelItems: DescendientesRecord[] = [];
  fascicleAvailabilityItems: FascicleRecord[] = [];
  shareText: string;
  socialButtons: string[];
  subjects: RecordSubject[] = [];
  isPPU: boolean;

  userRecommendations$: Observable<Record[]>;
  relatedItems$: Observable<Record[]>;
  alsoCheckedOut$: Observable<Record[]>;

  readOnly = true;
  showResourceDetails = false;
  showResourceCopies = true;
  showComments: boolean;
  showCopies: boolean;
  showAvailableCopies: boolean;
  showTotalCheckouts: boolean;
  showTotalViews: boolean;
  showUserRecommendations: boolean;
  isAuthorFollowed: boolean;
  showFollowAuthorButton: boolean;
  showUnfollowAuthorButton: boolean;
  showItemsSection: boolean;

  showTopNavigation: boolean;
  isBot: boolean;
  bookclubName: string;
  showGallery: boolean;
  showPhysicalAvailabilityTable: boolean;
  showLowerLevelItemsTable: boolean;
  showFascicleAvailabilityTable: boolean;
  showGalleryCallback: () => void;

  customerType: CustomerType;
  dummyCollection: Collection;
  recordCoveImageUrl: string;

  readonly descExpanded = signal<boolean>(false);
  readonly descExpandedLabel = computed(() => this.descExpanded() ? 'general.viewLess' : 'general.viewMore');

  constructor(
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private userService: UserService,
    private localeService: LocaleService,
    private configService: ConfigService,
    private broadcastService: BroadcastService,
    private dialog: MatDialog,
    private location: Location,
    private title: Title,
    private jsonLDService: JsonLDService,
    private seoService: SEOService,
    private playerUrlAnnotationService: PlayerUrlAnnotationService
  ) {
    this.isBot = Util.isBot();
    this.user = authService.getLoggedUser();
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.state = this.router.getCurrentNavigation().extras.state as SearchState;
    this.showComments = !this.isBot;
    this.showTotalViews = this.configService.get().showResourceTotalViews;
    this.showTopNavigation = this.state && this.state.index !== undefined;
    this.socialButtons = this.configService.shareIcons;
    this.showUserRecommendations =
      this.configService.get().showRecommendations && this.user && this.user.showRecommendations;
    this.buttonsConfig = new InfoButtonConfiguration(configService.get().showReturnLoanButton);
    this.bookclubName = configService.get().bookClubName;
    this.isAuthorFollowed = false;
    this.dummyCollection = { id: -1, name: '', records: [] };

    this.broadcastService.authorUnfollowed$.subscribe(() => {
      this.record.authorFollowedByActiveUser = false;
      this.configAuthorFollowingFeatureButtons();
    });
  }

  ngOnDestroy(): void {
    Util.removeAction();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.accessibilityAutofocus?.nativeElement.focus();
    }, 100);
  }

  readonly recordResolved$: Observable<Record> = this.route.data.pipe(
    pluck('recordResolved'),
    tap((record) => {
      this.updatePageMeta(record);
    })
  );

  updatePageMeta(recordResolved: Record) {
    this.record = recordResolved;
    this.setMetaTagsRecord();
  }

  ngOnInit() {
    this.recordResolved$.subscribe((record) => {
      this.record = record;
      this.setMetaTagsRecord();
    });

    this.loading = true;
    this.route.params.subscribe((params) => {
      const slug: string = params.slug;
      this.id = slug.split('-').pop();

      if (this.user && Number(this.id)) {
        this.readOnly = false;
        this.userRecommendations$ = this.apiService
          .getUserRecommendations(this.user.id, this.id)
          .pipe(map((r) => this.parseRecommendations(r)));
      }

      this.processRecord();
    });

    this.customerType = this.configService.customerType;

    this.showGalleryCallback = () => {
      this.getRecordImages();
    };

    const record = this.record as Record & { coverUrls: { [key: string]: string } };
    const xLarge = record.coverUrls['x-large'];

    this.recordCoveImageUrl = xLarge && xLarge !== '' ? xLarge : record.originalImageUrl;
  }

  processRecord() {
    this.commentsFacade.getCommentsByRecordId(this.record.id);
    this.showResourceDetails = this.shouldShowResourceDetails();
    this.showResourceCopies = !this.record.externalLink && !this.record.resourceTypes.includes('TIPO_IMAGEN');
    if (this.record.externalLink) {
      this.record.externalLink = this.record.externalLink.replace('#!/', '');
    }
    this.shareText = this.localeService.getLocalizedShareText(this.record, this.configService.get().title);
    this.isPPU = this.record.payPerCheckout || this.record.ppulicenseComplete;
    this.subjects = this.record.subjects.map((s) => {
      const key = `subjects.${Util.formatSubject(s)}`;
      const translated = this.localeService.instant(key);

      return { term: s, label: translated === key ? s : translated };
    });

    if (!this.isBot) {
      this.getRecordImages();
      this.getRecordRating();
      this.getRecordPhysicalAvailability();

      let from: FromScreen = 'RECORD_SCREEN';

      if (this.state) {
        from = this.state.from;
      }

      this.apiService.recordHit(this.record.id, this.record.title, from);
      // this.analytics.event('visita', this.record.title);

      this.relatedItems$ = this.apiService
        .getRecordRelatedRecommendations(this.id, RecommendationType.RELATED_ITEMS)
        .pipe(map((r) => this.parseRecommendations(r)));

      this.alsoCheckedOut$ = this.apiService
        .getRecordRelatedRecommendations(this.id, RecommendationType.ALSO_CHECKED_OUT)
        .pipe(map((r) => this.parseRecommendations(r)));
    }

    if (!this.isBot && this.record.active) {
      if (!this.record.availableIssueDates.length) {
        this.updateAvailability();
      }

      if (this.record.availableIssueDates.length && !this.issueDate) {
        this.route.queryParams.pipe().subscribe((queryParams) => {
          if (queryParams.issueDate) {
            this.issueDate = queryParams.issueDate;
          } else {
            this.issueDate = Util.getLastIssueDate(this.record);
          }
          this.updateAvailability();
        });
      }
    }

    this.showCopies = this.configService.get().showCopies && this.record.active;
    this.setShowAvailableCopies();
    this.showTotalCheckouts = this.configService.get().showTotalCheckouts && this.record.totalCheckouts > 0;
    this.showItemsSection = this.record.containerData != null;
    this.configAuthorFollowingFeatureButtons();

    setTimeout(() => {
      Util.runAction(this.user, this.playerUrlAnnotationService, this.record);
    }, 1000);

    this.route.queryParams.pipe().subscribe((queryParams) => {
      if (queryParams.annotationSharedId) {
        if (this.user || this.authService.isAuthenticated()) {
          this.playerUrlAnnotationService
            .getPlayerUrlAnnotation(this.record.id, queryParams.annotationSharedId)
            .subscribe((url) => window.open(url, '_self'));
        } else {
          const action: Action = {
            type: ActionType.OPEN_ANNOTATION,
            recordId: this.record.id,
            annotationId: queryParams.annotationSharedId,
          };
          Util.setAction(action);

          const customLoginUrl = this.configService.get().customLoginUrl;
          if (customLoginUrl) {
            this.authService.getExternalLoginUrl(customLoginUrl).subscribe((url) => {
              window.location.href = url;
            });
          } else {
            this.dialog
              .open(LoginDialogComponent)
              .afterClosed()
              .subscribe((value) => {
                if (value) {
                  (window as any).location.reload();
                }
              });
          }
        }
      }

      if (queryParams.issueDate && this.issueDate !== queryParams.issueDate) {
        this.issueDate = queryParams.issueDate;
        this.updateAvailability();
      }

      if (queryParams.rhId) {
        this.rhId = queryParams.rhId;
        this.rssItemSelectedFromUrl = true;
        this.apiService.getRssItem(this.record.id, this.rhId).subscribe((rssItem) => {
          Util.prepareRssItem(rssItem);
          this.selectRssItem(rssItem);
          this.loading = false;
        });
      } else {
        this.loading = false;
      }
    });
  }

  updateAvailability() {
    if (!this.record) {
      return;
    }

    if (this.record.container && this.selectedRssItem == null) {
      return;
    }

    let userId: string;

    if (this.user) {
      userId = this.user.id;
    }

    this.apiService.getRecordAvailability(this.id, userId, this.issueDate, this.rhId).subscribe((availability) => {
      this.availability = availability;
      if (this.selectedRssItem) {
        this.selectedRssItem.availability = availability;
      } else {
        this.record.availability = availability;
      }
    });
  }

  loadMoreComments() {
    this.commentsFacade.getCommentsByRecordId(this.record.id);
  }

  handleClick(event: string) {
    if (event === 'copy') {
      Util.copyToClipboard(window.location.href);
      this.toastService.showToast('messages.copied', 'info');
    }
  }

  getCurrentRecordIndex(): number {
    return this.state.index + 1 + this.state.page * this.state.pageSize;
  }

  configAuthorFollowingFeatureButtons() {
    this.showFollowAuthorButton =
      this.authService.isAuthenticated() && this.record.author.length > 0 && !this.record.authorFollowedByActiveUser;
    this.showUnfollowAuthorButton = this.authService.isAuthenticated() && this.record.authorFollowedByActiveUser;
  }

  getRecordImages() {
    if (this.record.resourceTypes.includes('TIPO_IMAGEN')) {
      this.apiService.getRecordInfo(this.record.id, 'IMAGE').subscribe((images) => {
        if (this.authService.isAuthenticated()) {
          this.images.push(...images.map((i) => i.path));
          this.showGallery = true;
        }
      });
    }
  }

  getRecordPhysicalAvailability() {
    if (this.record.resourceTypes.includes('TIPO_FISICO')) {
      this.apiService.getPhysicalRecordInfo(this.record.id, 'PHYSICAL').subscribe((physicalRecordItem) => {
        const physicalItem = physicalRecordItem.generalInfoList;
        const fascicleItem = physicalRecordItem.physicalInfoFasciclesList;
        const lowerLevelItem = physicalRecordItem.descendientesInfoList;

        this.physicalParentId = physicalRecordItem.upperLevelRecordId;
        this.physicalParentTitle = physicalRecordItem.upperLevelTitle;

        if (physicalItem) {
          this.physicalAvailabilityItems = [...physicalItem];
          this.showPhysicalAvailabilityTable = true;
        }
        if (lowerLevelItem) {
          this.physicalLowerLevelItems = [...lowerLevelItem];
          this.showLowerLevelItemsTable = true;
        }
        if (fascicleItem) {
          this.fascicleAvailabilityItems = [...fascicleItem];
          this.showFascicleAvailabilityTable = true;
        }

        if (this.record.resourceTypes.length === 1) {
          this.showAvailableCopies = false;
        }
      });
    }
  }

  private setShowAvailableCopies() {
    this.showAvailableCopies = this.configService.get().showAvailableCopies && this.record.active;
  }

  getRecordRating() {
    let userId: string;

    if (this.user) {
      userId = this.user.id;
    }

    this.apiService
      .getRecordRating(this.record.id, this.record.isbn, userId)
      .subscribe((rating) => (this.rating = rating));
  }

  onRecordRated(rating: Rating) {
    this.rating = rating;
  }

  buildTopicFacetQuery(facet: string): object {
    return Util.buildTopicFacetQuery(facet);
  }

  buildPpuFacetQuery(): object {
    return Util.buildPpuFacetQuery();
  }

  onItemSelected(record: Record) {
    this.router.navigate(['/info', this.getSlug(record)]);
  }

  getSlug(record: Record): string {
    return Util.slugifyRecord(record);
  }

  getExperienceLink(title: string, recordId: string): string {
    return Util.slugify(title) + '-' + recordId;
  }

  goToWikipedia(author: string) {
    const lang = this.localeService.language;
    const url = `https://${lang}.wikipedia.org/wiki/Special:Search?search=${author}`;
    window.open(url, '_blank', 'noopener');
  }

  searchByAuthor(author: string): { query: string } {
    return { query: `author:"${author}"` };
  }

  followAuthor() {
    if (this.record.author != null) {
      this.userService.followAuthor(this.record.author).subscribe(() => {
        this.record.authorFollowedByActiveUser = true;
        this.configAuthorFollowingFeatureButtons();
      });
    }
  }

  unfollowAuthor() {
    if (this.record.author != null) {
      this.userService.unfollowAuthor(this.record.author).subscribe();
    }
  }

  selectRssItem(item: RssItem) {
    this.selectedRssItem = item;
    this.rhId = item.rhId;
    this.showTopNavigation = false;
    this.rssItemSelectedFromUrl = false;
    this.location.replaceState(`${window.location.pathname}?rhId=${item.rhId}`);
    this.showResourceDetails = this.shouldShowResourceDetails();
    this.updateAvailability();
  }

  unselectRssItem() {
    this.selectedRssItem = null;
    this.rhId = null;
    this.showTopNavigation = this.state && this.state.index !== undefined;
    this.location.replaceState(window.location.pathname.split('?rhId=')[0]);
    this.showResourceDetails = this.shouldShowResourceDetails();
  }

  private shouldShowResourceDetails() {
    return this.selectedRssItem != null || (!this.record.containerData && this.record.resourceTypes.length > 0);
  }

  private parseRecommendations(recommendations: Recommendation[]): Record[] {
    return recommendations !== null ? this.toRecord(recommendations) : [];
  }

  private toRecord(recommendations: Recommendation[]): Record[] {
    return recommendations
      .map((r) => ({
        id: r.recordId,
        active: true,
        title: r.title,
        author: r.author,
        description: r.description,
        coverImageUrl: r.coverImageUrl,
        resourceTypes: r.resourceTypes,
        specialFormat: r.specialFormat,
      }))
      .map((r) => Util.prepareRecord(r));
  }

  private setMetaTagsRecord() {
    let newTitle = this.record.title;
    if (this.configService.get() != undefined) {
      newTitle = `${this.configService.get().title} - ${this.record.title}`;
    }

    let newDescription = this.record.description;

    newDescription = newDescription
      .replace(/<\/?[^>]+(>|$)/g, '')
      .replace(/(\r\n|\n|\r)/gm, '')
      .replace(/(&nbsp;)+/g, ' ')
      .trim();

    const ogDescription = newDescription;
    if (newDescription.length > 197) {
      newDescription = newDescription.substr(0, 197).concat('...');
    }
    this.title.setTitle(newTitle);

    this.jsonLDService.insertBookLD(this.record, newDescription);

    this.seoService.generateSEOTags(
      newTitle,
      newDescription,
      window.location.href.split('?')[0],
      this.record.coverImageUrl,
      ''
    );
  }

  openRootComment() {
    this.commentsFacade.openTextarea(this.textareaId());
  }

  cancelRootComment() {
    this.commentsFacade.closeTextarea();
  }

  submitRootComment({ comment }: SubmitReplyEvent) {
    this.commentsFacade.postComment(this.record.id, this.user.id, comment);
    this.commentsFacade.closeTextarea();
  }
}
